import './Roulette.sass';
import './Roulette.css';

import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { RouletteProps } from '$shared/components/Roulette/Roulette.interface';
import { Wheel } from './Wheel/Wheel';
import { ReactComponent as PointerSvg } from './assets/pointer.svg';
import { Button } from '$uikit';
import { AuthPopoverState, CurrencyEnum } from '$store/models';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { useDispatch, useSelector } from 'react-redux';
import { ColorsEnum } from '$shared/constants/colors';
import {
  rouletteActions,
  rouletteGameInProgressSelector,
  rouletteLotsSelector,
  roulettePrizeSelector,
  rouletteSelector,
} from '$store/roulette';
import { RootState } from '$store/rootReducer';
import { showModal } from '$navigation/helper';
import { batchActions } from '$store';
import { useTranslator } from '$hooks';
import { formatCurrency, statReachGoal } from 'utils';
import { profileActions } from '$store/profile';
import { authSelector } from '$store/auth';

const colors = {
  [ColorsEnum.PURPLE]: ['#AA61F2', '#6950E6'],
  [ColorsEnum.ORANGE]: ['#FFC44D', '#FF8040'],
  [ColorsEnum.PINK]: ['#F261CE', '#E6508E'],
  [ColorsEnum.CYAN]: ['#2EC7E6', '#2E8AE6'],
  [ColorsEnum.RED]: ['#F26F55', '#E64560'],
  [ColorsEnum.GREEN]: ['#99CC66', '#56BF56'],
  [ColorsEnum.BLUE]: ['#619DF2', '#505DE6'],
  [ColorsEnum.GREY]: ['#9AA6B8', '#78818F'],
};

export const Roulette: FC<RouletteProps> = ({ postId, roulette, canRotate }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { isLogged } = useSelector(authSelector);
  const firstRender = useRef(true);

  const lastRotationTime = useSelector(
    (state: RootState) => state.roulette.lastRotationTime,
  );
  const currentLotIndex = useSelector((state: RootState) =>
    rouletteLotsSelector(state, roulette.rouletteId),
  );

  const gameInProgressId = useSelector(rouletteGameInProgressSelector);
  const gameInProgress = gameInProgressId && gameInProgressId === roulette.rouletteId;

  useEffect(() => {
    if (!firstRender.current) {
      dispatch(
        batchActions(
          rouletteActions.setRoulette(roulette),
          rouletteActions.setPostId({
            rouletteId: roulette.rouletteId,
            postId,
          }),
        ),
      );
    } else {
      firstRender.current = false;
    }
  }, [roulette, currentLotIndex]);

  const handleSpin = useCallback(() => {
    statReachGoal('roulette_click_rotate', {
      rouletteId: roulette.rouletteId,
    });

    if (isLogged) {
      dispatch(
        balancePayActions.open({
          amount: roulette.price,
          action: BalancePayAction.ROULETTE,
          objectId: roulette.rouletteId,
          extraId: postId,
        }),
      );
    } else {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
    }
  }, [isLogged, roulette]);

  const normalizeLots = useMemo(() => {
    const { lots } = roulette;
    const min = 20;
    return lots.length >= min
      ? lots
      : Array(Math.ceil(Math.max(min / lots.length, 2)))
          .fill(lots)
          .flat();
  }, [roulette.lots]);

  const postDecoratorBackground = useMemo(() => {
    return `conic-gradient(from var(--border-angle), ${[
      ...roulette.lots.map((i) => colors[i.color][1]),
      colors[roulette.lots[0].color][1],
    ]})`;
  }, [roulette]);

  return (
    <div className="Roulette">
      <Wheel
        onTouch={() => {
          statReachGoal('roulette_touch_rotate', {
            rouletteId: roulette.rouletteId,
          });
        }}
        gameInProgress={!!gameInProgress}
        lastRotationTime={lastRotationTime}
        onPrize={() => {
          showModal('roulette_lot');
          dispatch(rouletteActions.setGameProgress(null));
        }}
        currentLotIndex={currentLotIndex}
        lots={normalizeLots.map((l, index) => ({
          id: index,
          colors: colors[l.color],
          title: l.title,
        }))}
      />
      <div className="Roulette__pointer">
        <PointerSvg />
      </div>
      {canRotate ? (
        !gameInProgress && (
          <Button mode="pay" onClick={handleSpin} className="Roulette__spinButton">
            {t('roulette_spin_button', {
              amount: formatCurrency(roulette.price, CurrencyEnum.EUR),
            })}
          </Button>
        )
      ) : (
        <div className="Roulette__earned">
          {t('roulette_earned_amount', {
            amount: formatCurrency(roulette.price * roulette.counter, CurrencyEnum.EUR),
          })}
        </div>
      )}
      {gameInProgress && (
        <div
          style={
            {
              backgroundImage: postDecoratorBackground,
              '--accent-color': colors[roulette.lots[0].color][0],
            } as React.CSSProperties
          }
          className="Roulette__postDecorator"
        ></div>
      )}
    </div>
  );
};
